import React, { useState, useEffect } from 'react';
import { fetchAPI, postAPI } from '../../utils/utils';
import { getEventCategoryOptions } from './calendarCategories';
import '../onboarding/OnboardingWizard.css';
import {
    getCalendarUpdateErrorMessage,
    isCalendarAutomatic,
    isCalendarPublic,
    setCalendarAutomatic,
    setCalendarPublic,
    setCalendarTarget,
} from './calendarVisibility';

function CalendarsEditor({ mode = 'add', eventCategories }) {
    const [calendar, setCalendar] = useState({
        ids: [],
        calendar_targets: {}
    });
    const [editMode, setEditMode] = useState(false);
    const [newCalendarId, setNewCalendarId] = useState('');
    const [copiedEmail, setCopiedEmail] = useState(false);
    const [calendarError, setCalendarError] = useState(null);
    const allowedTargets = ['tècnica', 'junta'];

    const ALETA_EMAIL = 'calendar-key@calendar-sample-370523.iam.gserviceaccount.com';

    const parseCalendarIds = (value) => value
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

    useEffect(() => {
        fetchAPI('/calendar_keys', data => setCalendar(data));
    }, [editMode]);

    const handleCalendarError = error => {
        setCalendarError(getCalendarUpdateErrorMessage(error));
    };

    const updateCalendar = () => {
        if (calendar.ids.some(id => id.trim() === '')) {
            alert('Assegureu-vos que cap ID de calendari estigui buit');
            return;
        }
        if (window.confirm('Esteu segurs que voleu guardar aquests canvis?')) {
            setCalendarError(null);
            postAPI(
                '/update_calendar_keys',
                { calendar },
                () => window.location.reload(),
                handleCalendarError
            );
        }
    };

    const handleIdChange = (event, index) => {
        const newIds = [...calendar.ids];
        newIds[index] = event.target.value;
        setCalendar(prevState => ({ ...prevState, ids: newIds }));
    };

    const handleAddId = () => {
        if (!newCalendarId.trim()) {
            alert('Introdueix un ID de calendari vàlid');
            return;
        }

        const calendarIdsToAdd = parseCalendarIds(newCalendarId);
        if (calendarIdsToAdd.length === 0) {
            alert('Introdueix un ID de calendari vàlid');
            return;
        }

        const updatedCalendar = {
            ...calendar,
            ids: [...calendar.ids, ...calendarIdsToAdd]
        };

        setCalendarError(null);
        postAPI(
            '/update_calendar_keys',
            { calendar: updatedCalendar },
            data => {
                setCalendar(data);
                setNewCalendarId('');
            },
            handleCalendarError
        );
    };

    const handleRemoveId = (index) => {
        const newIds = [...calendar.ids];
        if (newIds[index] !== '') {
            if (window.confirm('Esteu segurs que voleu eliminar aquesta ID?')) {
                const removedId = newIds[index];
                newIds.splice(index, 1);
                const updatedCalendarTargets = { ...(calendar.calendar_targets || {}) };
                delete updatedCalendarTargets[removedId];
                const updatedCalendar = {
                    ...calendar,
                    ids: newIds,
                    calendar_targets: updatedCalendarTargets
                };

                setCalendarError(null);
                postAPI(
                    '/update_calendar_keys',
                    { calendar: updatedCalendar },
                    data => setCalendar(data),
                    handleCalendarError
                );
            }
        } else {
            newIds.splice(index, 1);
            setCalendar(prevState => ({ ...prevState, ids: newIds }));
        }
    };

    const handleTargetChange = (checked, key, target) => {
        setCalendar(prevState => {
            const prevTargets = prevState?.calendar_targets || {};
            return {
                ...prevState,
                calendar_targets: setCalendarTarget(prevTargets, key, target, checked)
            };
        });
    };

    const handlePublicVisibility = key => {
        setCalendar(prevState => ({
            ...prevState,
            calendar_targets: setCalendarPublic(prevState?.calendar_targets, key)
        }));
    };

    const handleAutomaticVisibility = key => {
        setCalendar(prevState => ({
            ...prevState,
            calendar_targets: setCalendarAutomatic(prevState?.calendar_targets, key)
        }));
    };

    const calendarErrorAlert = calendarError ? (
        <div
            role="alert"
            style={{
                background: '#f8d7da',
                border: '1px solid #f5c2c7',
                borderRadius: 6,
                color: '#842029',
                marginBottom: 16,
                padding: '12px 16px',
            }}
        >
            {calendarError}
        </div>
    ) : null;

    const copyEmail = () => {
        navigator.clipboard.writeText(ALETA_EMAIL);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
    };

    // Add mode - clean step-by-step guide
    if (mode === 'add') {
        return (
            <div className="onboarding-step-content">
                <div className="step-description">
                    <p>
                        L'Aleta es sincronitza automàticament amb Google Calendar. 
                        Segueix aquests passos per connectar el calendari de la colla.
                    </p>
                </div>

                {calendarErrorAlert}

                {calendar.ids.length > 0 ? (
                    <div style={{ 
                        background: '#e8f5e9', 
                        border: '1px solid #c8e6c9', 
                        borderRadius: 6, 
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>✓</span>
                        <span style={{ color: '#2e7d32', fontWeight: 500 }}>
                            {calendar.ids.length === 1 
                                ? '1 calendari connectat' 
                                : `${calendar.ids.length} calendaris connectats`}
                        </span>
                        <span style={{ color: '#666', fontSize: '0.85rem', marginLeft: 'auto' }}>
                            Gestionar a "Calendaris"
                        </span>
                    </div>
                ) : null}

                <div className="tutorial-section" open>
                    <details open>
                        <summary style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>
                            📋 Com connectar un calendari de Google
                        </summary>
                        <div className="tutorial-steps" style={{ marginTop: 16 }}>
                            
                            {/* Optional: Create calendar */}
                            <details className="tutorial-create-calendar">
                                <summary>No tens un calendari? Mira aquests passos</summary>
                                <div className="tutorial-create-steps">
                                    <div className="tutorial-step">
                                        <div className="tutorial-step-number">A</div>
                                        <div className="tutorial-step-content">
                                            <strong>Crea un nou calendari</strong>
                                            <p>
                                                A <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a>, 
                                                fes clic al botó "+" a l'esquerra per crear un nou calendari.
                                            </p>
                                            <img className="tutorial-image" src={require('./tutorial/steps/1.png')} alt="Crear calendari" style={{ maxWidth: '80%' }} />
                                        </div>
                                    </div>
                                    <div className="tutorial-step">
                                        <div className="tutorial-step-number">B</div>
                                        <div className="tutorial-step-content">
                                            <strong>Posa-li nom</strong>
                                            <p>Dona-li un nom descriptiu (ex: "Calendari Colla").</p>
                                            <img className="tutorial-image" src={require('./tutorial/steps/2.png')} alt="Nom del calendari" style={{ maxWidth: '80%' }} />
                                        </div>
                                    </div>
                                </div>
                            </details>

                            {/* Step 1: Open settings */}
                            <div className="tutorial-step">
                                <div className="tutorial-step-number">1</div>
                                <div className="tutorial-step-content">
                                    <strong>Obre la configuració del calendari</strong>
                                    <p>
                                        A <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a>, 
                                        fes clic als tres punts (⋮) del calendari que vols connectar i selecciona "Configuració i compartició".
                                    </p>
                                    <img className="tutorial-image" src={require('./tutorial/steps/3.png')} alt="Configuració del calendari" style={{ maxWidth: '80%' }} />
                                </div>
                            </div>

                            {/* Step 2: Share with Aleta */}
                            <div className="tutorial-step">
                                <div className="tutorial-step-number">2</div>
                                <div className="tutorial-step-content">
                                    <strong>Comparteix el calendari amb l'Aleta</strong>
                                    <p>
                                        A l'apartat "Comparteix amb persones concretes", afegeix aquest correu electrònic:
                                    </p>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 10, 
                                        marginTop: 8 
                                    }}>
                                        <code className="tutorial-email" style={{ flex: 1, margin: 0 }}>
                                            {ALETA_EMAIL}
                                        </code>
                                        <button 
                                            onClick={copyEmail}
                                            style={{
                                                padding: '8px 14px',
                                                background: copiedEmail ? '#28a745' : '#2d7dd2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {copiedEmail ? '✓ Copiat!' : 'Copiar'}
                                        </button>
                                    </div>
                                    <img className="tutorial-image" src={require('./tutorial/steps/4.png')} alt="Compartir calendari" style={{ maxWidth: '80%' }} />
                                </div>
                            </div>

                            {/* Step 3: Get calendar ID */}
                            <div className="tutorial-step">
                                <div className="tutorial-step-number">3</div>
                                <div className="tutorial-step-content">
                                    <strong>Copia l'ID del calendari</strong>
                                    <p>
                                        Baixa fins a "Integrar el calendari" i copia l'ID del calendari 
                                        (acaba en <em>@gmail.com</em> o <em>@group.calendar.google.com</em>).
                                    </p>
                                    <img className="tutorial-image" src={require('./tutorial/steps/5.png')} alt="ID del calendari" style={{ maxWidth: '80%' }} />
                                </div>
                            </div>

                            {/* Step 4: Add to Aleta */}
                            <div className="tutorial-step">
                                <div className="tutorial-step-number">4</div>
                                <div className="tutorial-step-content">
                                    <strong>Enganxa l'ID aquí</strong>
                                    <p>
                                        Enganxa l'ID del calendari que has copiat i fes clic a "Afegir calendari".
                                    </p>
                                    <div className="add-calendar-form" style={{ marginTop: 12, border: '2px solid #2d7dd2' }}>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                value={newCalendarId}
                                                onChange={(e) => setNewCalendarId(e.target.value)}
                                                placeholder="Enganxa l'ID del calendari aquí"
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddId()}
                                            />
                                            <button className="add-btn" onClick={handleAddId}>
                                                Afegir calendari
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </details>
                </div>

            </div>
        );
    }

    // Edit mode - for managing existing calendars
    return (
        <div style={{ marginTop: 16 }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
            }}>
                <h3 style={{ margin: 0 }}>Gestionar calendaris</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                        onClick={() => setEditMode(!editMode)}
                        style={{
                            padding: '8px 16px',
                            background: editMode ? '#dc3545' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: 5,
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        {editMode ? 'Cancel·lar' : 'Editar'}
                    </button>
                    {editMode ? (
                        <button 
                            onClick={updateCalendar}
                            style={{
                                padding: '8px 16px',
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: 5,
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                            }}
                        >
                            Guardar canvis
                        </button>
                    ) : null}
                </div>
            </div>

            {calendarErrorAlert}

            {calendar.ids.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    No hi ha cap calendari configurat. Afegeix-ne un des de la pestanya "Afegir".
                </p>
            ) : null}

            {calendar.ids.map((id, index) => (
                <div 
                    key={index}
                    style={{
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 12
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: editMode ? 12 : 0
                    }}>
                        {editMode ? (
                            <input 
                                type="text"
                                value={id}
                                placeholder='ID del calendari'
                                onChange={(e) => handleIdChange(e, index)}
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem'
                                }}
                            />
                        ) : (
                            <code style={{ 
                                flex: 1, 
                                fontSize: '0.85rem', 
                                color: '#555',
                                wordBreak: 'break-all'
                            }}>
                                {id}
                            </code>
                        )}
                        {editMode ? (
                            <button 
                                onClick={() => handleRemoveId(index)}
                                style={{
                                    padding: '8px 14px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Eliminar
                            </button>
                        ) : null}
                    </div>

                    {!editMode ? (
                        <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#555' }}>
                            Categoria dels esdeveniments nous: <strong>{calendar.calendar_event_categories?.[id]
                                ? getEventCategoryOptions(eventCategories || calendar.event_categories, calendar.calendar_event_categories[id]).find(option => option.value === calendar.calendar_event_categories[id])?.label
                                : 'Automàtica (segons el títol)'}</strong>
                        </p>
                    ) : null}

                    {editMode ? (
                        <div style={{ 
                            borderTop: '1px solid #dee2e6', 
                            paddingTop: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6
                        }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#555' }}>
                                Visibilitat per defecte dels esdeveniments nous:
                            </span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                                <input
                                    type="radio"
                                    name={`calendar-visibility-${id}`}
                                    checked={isCalendarAutomatic(calendar?.calendar_targets, id)}
                                    onChange={() => handleAutomaticVisibility(id)}
                                />
                                <span>Automàtic (segons el títol)</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                                <input
                                    type="radio"
                                    name={`calendar-visibility-${id}`}
                                    checked={isCalendarPublic(calendar?.calendar_targets, id)}
                                    onChange={() => handlePublicVisibility(id)}
                                />
                                <span>Públic (tothom ho veu)</span>
                            </label>
                            {allowedTargets.map(target => (
                                <label key={target} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={calendar?.calendar_targets?.[id]?.includes(target) || false}
                                        onChange={(e) => handleTargetChange(e.target.checked, id, target)}
                                    />
                                    <span>Privat per {target}</span>
                                </label>
                            ))}
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                                Categoria dels esdeveniments nous
                                <select
                                    aria-label="Categoria dels esdeveniments nous"
                                    value={calendar.calendar_event_categories?.[id] || ''}
                                    onChange={event => {
                                        const category = event.target.value;
                                        setCalendar(previous => {
                                            const categories = { ...previous.calendar_event_categories };
                                            if (category) categories[id] = category;
                                            else delete categories[id];
                                            return { ...previous, calendar_event_categories: categories };
                                        });
                                    }}
                                >
                                    <option value="">Automàtica (segons el títol)</option>
                                    {getEventCategoryOptions(eventCategories || calendar.event_categories, calendar.calendar_event_categories?.[id] || 'general').map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {!calendar.calendar_event_categories?.[id] ? <small>Detecta músics, gralles, tabals o grallers al títol; la resta es classifica com a General.</small> : null}
                                <small>Permet que cada membre triï quins esdeveniments vol veure a l’app. S’aplica a assajos, actuacions i activitats, i no modifica qui hi té accés. Els esdeveniments ja importats es classifiquen a Esdeveniments.</small>
                            </label>
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

export default CalendarsEditor;
